import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { clientEngagementsService } from '@/features/client/services/client.engagements.service'
import { ticketingService } from '@/features/ticketing/services/ticketing.service'
import { getOrdersByUserId } from '@/features/shop/services/shop.service'
import type { ClientEngagementDocument, TicketOrderDocument } from '@/types'
import type { ShopOrderDocument } from '@/features/shop/types'

export type ActivityItem =
  | { kind: 'engagement'; date: Date; data: ClientEngagementDocument & { id: string } }
  | { kind: 'ticket-order'; date: Date; data: TicketOrderDocument & { id: string } }
  | { kind: 'shop-order'; date: Date; data: ShopOrderDocument & { id: string } }

interface UseMyActivityResult {
  items: ActivityItem[]
  loading: boolean
  errors: { engagements?: string; tickets?: string; orders?: string }
}

function tsToDate(value: unknown): Date {
  if (value instanceof Date) return value
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate()
  }
  if (typeof value === 'string') return new Date(value)
  return new Date(0)
}

export function useMyActivity(): UseMyActivityResult {
  const { user } = useAuth()
  const [items, setItems] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState<UseMyActivityResult['errors']>({})

  useEffect(() => {
    if (!user) {
      setItems([])
      setLoading(false)
      return
    }
    setLoading(true)
    setErrors({})

    const engagementsP = clientEngagementsService
      .getEngagementsForUser(user.uid)
      .then((rows): ActivityItem[] =>
        rows.map((e) => ({
          kind: 'engagement',
          date: e.scheduledDate ? new Date(e.scheduledDate) : tsToDate(e.createdAt),
          data: e,
        }))
      )
      .catch((err: unknown) => {
        setErrors((s) => ({ ...s, engagements: formatUserFacingError(err, 'Failed to load engagements') }))
        return [] as ActivityItem[]
      })

    const ticketsP = ticketingService
      .getOrdersByUserId(user.uid)
      .then((rows): ActivityItem[] =>
        rows.map((o) => ({ kind: 'ticket-order', date: tsToDate(o.createdAt), data: o }))
      )
      .catch((err: unknown) => {
        setErrors((s) => ({ ...s, tickets: formatUserFacingError(err, 'Failed to load tickets') }))
        return [] as ActivityItem[]
      })

    const ordersP = getOrdersByUserId(user.uid)
      .then((rows): ActivityItem[] =>
        rows.map((o) => ({ kind: 'shop-order', date: tsToDate(o.createdAt), data: o }))
      )
      .catch((err: unknown) => {
        setErrors((s) => ({ ...s, orders: formatUserFacingError(err, 'Failed to load orders') }))
        return [] as ActivityItem[]
      })

    Promise.all([engagementsP, ticketsP, ordersP])
      .then(([a, b, c]) => {
        const merged = [...a, ...b, ...c].sort((x, y) => y.date.getTime() - x.date.getTime())
        setItems(merged)
      })
      .finally(() => setLoading(false))
  }, [user])

  return { items, loading, errors }
}
